import { useState, useEffect } from 'react';
import { Search, SlidersHorizontal, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { provinces, activities, campTypes, ageGroups, verificationStatuses } from '../lib/utils';

// Collapsible Filter Section Component
const FilterSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full py-3 text-left font-medium hover:text-primary transition-colors"
      >
        {title}
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {isOpen && (
        <div className="pb-4 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

// Checkbox Group Component
const CheckboxGroup = ({ options, selected, onChange, columns = 2 }) => {
  const toggleOption = (value) => {
    const newSelected = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(newSelected);
  };

  return (
    <div className={`grid grid-cols-${columns} gap-2`}>
      {options.map((option) => {
        const value = typeof option === 'object' ? option.value : option;
        const label = typeof option === 'object' ? option.label : option;
        const isSelected = selected.includes(value);

        return (
          <label
            key={value}
            className="flex items-center gap-2 cursor-pointer text-sm hover:text-primary transition-colors"
          >
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => toggleOption(value)}
              className="h-4 w-4"
            />
            <span className={isSelected ? 'text-primary font-medium' : ''}>{label}</span>
          </label>
        );
      })}
    </div>
  );
};

export const SearchFilters = ({ 
  type = 'facilitator', 
  onFilterChange, 
  initialFilters = {} 
}) => {
  const [search, setSearch] = useState(initialFilters.search || '');
  const [selectedProvinces, setSelectedProvinces] = useState(
    initialFilters.province ? initialFilters.province.split(',') : []
  );
  const [selectedCampTypes, setSelectedCampTypes] = useState(
    initialFilters.camp_type ? initialFilters.camp_type.split(',') : []
  );
  const [selectedActivities, setSelectedActivities] = useState(
    initialFilters.activity ? initialFilters.activity.split(',') : []
  );
  const [selectedAgeGroups, setSelectedAgeGroups] = useState(
    initialFilters.age_group ? initialFilters.age_group.split(',') : []
  );
  const [selectedVerificationStatuses, setSelectedVerificationStatuses] = useState(
    initialFilters.verification_status ? initialFilters.verification_status.split(',') : []
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const handleSearch = () => {
    const filters = {
      search: search || undefined,
      province: selectedProvinces.length > 0 ? selectedProvinces.join(',') : undefined,
      camp_type: selectedCampTypes.length > 0 ? selectedCampTypes.join(',') : undefined,
      activity: selectedActivities.length > 0 ? selectedActivities.join(',') : undefined,
      age_group: selectedAgeGroups.length > 0 ? selectedAgeGroups.join(',') : undefined,
      verification_status: selectedVerificationStatuses.length > 0 ? selectedVerificationStatuses.join(',') : undefined,
    };
    
    Object.keys(filters).forEach(key => 
      filters[key] === undefined && delete filters[key]
    );
    
    onFilterChange(filters);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedProvinces([]);
    setSelectedCampTypes([]);
    setSelectedActivities([]);
    setSelectedAgeGroups([]);
    setSelectedVerificationStatuses([]);
    onFilterChange({});
  };

  const activeFilterCount = 
    selectedProvinces.length + 
    selectedCampTypes.length + 
    selectedActivities.length + 
    selectedAgeGroups.length + 
    selectedVerificationStatuses.length;

  const hasActiveFilters = search || activeFilterCount > 0;

  // Debounced search for text input
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Immediate update for multi-selects
  useEffect(() => {
    handleSearch();
  }, [selectedProvinces, selectedCampTypes, selectedActivities, selectedAgeGroups, selectedVerificationStatuses]);

  // Format age groups for display
  const ageGroupOptions = [
    { value: 'under_6', label: 'Under 6' },
    { value: '6-12', label: '6–12' },
    { value: '13-17', label: '13–17' },
    { value: '18+', label: '18+' },
    { value: 'all_ages', label: 'All ages' },
  ];

  // Province options
  const provinceOptions = provinces.map(p => ({ value: p, label: p }));

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={`Search ${type === 'camp' ? 'camps' : 'facilitators'}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 rounded-full"
            data-testid="search-input"
          />
        </div>
        
        {/* Mobile Filter Toggle */}
        <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <SheetTrigger asChild>
            <Button 
              variant="outline" 
              size="icon" 
              className="md:hidden rounded-full relative"
              data-testid="mobile-filter-toggle"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl">
            <SheetHeader>
              <SheetTitle className="flex items-center justify-between">
                <span>Filters</span>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                    Clear all
                  </Button>
                )}
              </SheetTitle>
            </SheetHeader>
            <div className="mt-6 overflow-y-auto max-h-[calc(85vh-100px)] pr-2">
              <MobileFilters
                type={type}
                selectedProvinces={selectedProvinces}
                setSelectedProvinces={setSelectedProvinces}
                selectedCampTypes={selectedCampTypes}
                setSelectedCampTypes={setSelectedCampTypes}
                selectedActivities={selectedActivities}
                setSelectedActivities={setSelectedActivities}
                selectedAgeGroups={selectedAgeGroups}
                setSelectedAgeGroups={setSelectedAgeGroups}
                selectedVerificationStatuses={selectedVerificationStatuses}
                setSelectedVerificationStatuses={setSelectedVerificationStatuses}
                provinceOptions={provinceOptions}
                ageGroupOptions={ageGroupOptions}
              />
              <div className="mt-6 pb-4">
                <Button 
                  onClick={() => setIsFilterOpen(false)} 
                  className="w-full rounded-full"
                >
                  Show Results
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Filters */}
      <div className="hidden md:block">
        <div className="bg-card rounded-2xl border border-border p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-sm">Filters</h3>
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="h-8 text-muted-foreground hover:text-foreground"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear all ({activeFilterCount})
              </Button>
            )}
          </div>

          <div className="space-y-1">
            {/* Province Filter */}
            <FilterSection title={`Province${selectedProvinces.length > 0 ? ` (${selectedProvinces.length})` : ''}`} defaultOpen={true}>
              <CheckboxGroup
                options={provinceOptions}
                selected={selectedProvinces}
                onChange={setSelectedProvinces}
                columns={3}
              />
            </FilterSection>

            {/* Camp Type Filter */}
            <FilterSection title={`Camp Type${selectedCampTypes.length > 0 ? ` (${selectedCampTypes.length})` : ''}`}>
              <CheckboxGroup
                options={campTypes}
                selected={selectedCampTypes}
                onChange={setSelectedCampTypes}
                columns={2}
              />
            </FilterSection>

            {/* Activities Filter - only for camps */}
            {type === 'camp' && (
              <FilterSection title={`Activities${selectedActivities.length > 0 ? ` (${selectedActivities.length})` : ''}`}>
                <CheckboxGroup
                  options={activities.map(a => ({ value: a, label: a }))}
                  selected={selectedActivities}
                  onChange={setSelectedActivities}
                  columns={2}
                />
              </FilterSection>
            )}

            {/* Age Groups Filter */}
            <FilterSection title={`Age Groups${selectedAgeGroups.length > 0 ? ` (${selectedAgeGroups.length})` : ''}`}>
              <CheckboxGroup
                options={ageGroupOptions}
                selected={selectedAgeGroups}
                onChange={setSelectedAgeGroups}
                columns={2}
              />
            </FilterSection>

            {/* Verification Status Filter */}
            <FilterSection title={`Status${selectedVerificationStatuses.length > 0 ? ` (${selectedVerificationStatuses.length})` : ''}`}>
              <CheckboxGroup
                options={verificationStatuses}
                selected={selectedVerificationStatuses}
                onChange={setSelectedVerificationStatuses}
                columns={3}
              />
            </FilterSection>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mobile Filter Component
const MobileFilters = ({
  type,
  selectedProvinces,
  setSelectedProvinces,
  selectedCampTypes,
  setSelectedCampTypes,
  selectedActivities,
  setSelectedActivities,
  selectedAgeGroups,
  setSelectedAgeGroups,
  selectedVerificationStatuses,
  setSelectedVerificationStatuses,
  provinceOptions,
  ageGroupOptions,
}) => {
  return (
    <div className="space-y-6">
      {/* Province */}
      <div>
        <h4 className="font-medium mb-3">Province</h4>
        <div className="grid grid-cols-2 gap-2">
          {provinceOptions.map((option) => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={selectedProvinces.includes(option.value)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSelectedProvinces([...selectedProvinces, option.value]);
                  } else {
                    setSelectedProvinces(selectedProvinces.filter(v => v !== option.value));
                  }
                }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Camp Type */}
      <div>
        <h4 className="font-medium mb-3">Camp Type</h4>
        <div className="grid grid-cols-2 gap-2">
          {campTypes.map((option) => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={selectedCampTypes.includes(option.value)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSelectedCampTypes([...selectedCampTypes, option.value]);
                  } else {
                    setSelectedCampTypes(selectedCampTypes.filter(v => v !== option.value));
                  }
                }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Activities - only for camps */}
      {type === 'camp' && (
        <div>
          <h4 className="font-medium mb-3">Activities</h4>
          <div className="grid grid-cols-2 gap-2">
            {activities.map((activity) => (
              <label key={activity} className="flex items-center gap-2 cursor-pointer text-sm">
                <Checkbox
                  checked={selectedActivities.includes(activity)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedActivities([...selectedActivities, activity]);
                    } else {
                      setSelectedActivities(selectedActivities.filter(v => v !== activity));
                    }
                  }}
                />
                <span>{activity}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Age Groups */}
      <div>
        <h4 className="font-medium mb-3">Age Groups</h4>
        <div className="grid grid-cols-2 gap-2">
          {ageGroupOptions.map((option) => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={selectedAgeGroups.includes(option.value)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSelectedAgeGroups([...selectedAgeGroups, option.value]);
                  } else {
                    setSelectedAgeGroups(selectedAgeGroups.filter(v => v !== option.value));
                  }
                }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Verification Status */}
      <div>
        <h4 className="font-medium mb-3">Verification Status</h4>
        <div className="grid grid-cols-2 gap-2">
          {verificationStatuses.map((option) => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={selectedVerificationStatuses.includes(option.value)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSelectedVerificationStatuses([...selectedVerificationStatuses, option.value]);
                  } else {
                    setSelectedVerificationStatuses(selectedVerificationStatuses.filter(v => v !== option.value));
                  }
                }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchFilters;
